import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const UserImportModal = ({ isOpen, onClose, onImport }) => {
  const [importStep, setImportStep] = useState('upload'); // upload, preview, processing, complete
  const [file, setFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const mockImportData = [
    {
      row: 1,
      name: 'Pierre Durand',
      email: 'p.durand@example.fr',
      role: 'Field Inspector',
      department: 'Field Operations',
      tenant: 'Qualitech Inspections',
      status: 'valid'
    },
    {
      row: 2,
      name: 'Claire Martin',
      email: 'c.martin@example.fr',
      role: 'Manager',
      department: 'Administration',
      tenant: 'Qualitech Inspections',
      status: 'valid'
    },
    {
      row: 3,
      name: 'Invalid User',
      email: 'invalid-email',
      role: 'Unknown Role',
      department: 'Unknown Dept',
      tenant: 'Unknown Tenant',
      status: 'error',
      errors: ['Invalid email format', 'Unknown role', 'Unknown department']
    }
  ];

  const csvTemplate = `Name,Email,Role,Department,Tenant
Jean Dupont,j.dupont@example.fr,Field Inspector,Field Operations,Qualitech Inspections
Marie Leblanc,m.leblanc@example.fr,Manager,Administration,Qualitech Inspections`;

  const handleFileSelect = (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Simulate file parsing
      setTimeout(() => {
        setImportData(mockImportData);
        setImportStep('preview');
      }, 1000);
    }
  };

  const handleDragOver = (event) => {
    event?.preventDefault();
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    const droppedFile = event?.dataTransfer?.files?.[0];
    if (droppedFile && droppedFile?.type === 'text/csv') {
      setFile(droppedFile);
      setTimeout(() => {
        setImportData(mockImportData);
        setImportStep('preview');
      }, 1000);
    }
  };

  const handleImport = async () => {
    setImportStep('processing');
    
    // Simulate import process
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setImportResults({
      total: importData?.length,
      successful: importData?.filter(item => item?.status === 'valid')?.length,
      failed: importData?.filter(item => item?.status === 'error')?.length,
      errors: importData?.filter(item => item?.status === 'error')
    });
    
    setImportStep('complete');
  };

  const handleClose = () => {
    setImportStep('upload');
    setFile(null);
    setImportData([]);
    setImportResults(null);
    setValidationErrors([]);
    onClose();
  };

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-300 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Import Users</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Bulk import users from CSV file
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {importStep === 'upload' && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-foreground mb-2">Import Instructions</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• CSV file with headers: Name, Email, Role, Department, Tenant</li>
                  <li>• Maximum 500 users per import</li>
                  <li>• Email addresses must be unique</li>
                  <li>• Roles and departments must exist in the system</li>
                </ul>
              </div>

              {/* Template Download */}
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="text-sm font-medium text-foreground">Download Template</p>
                  <p className="text-xs text-muted-foreground">Get a sample CSV file with the correct format</p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate} iconName="Download">
                  Template
                </Button>
              </div>

              {/* File Upload */}
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-micro"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef?.current?.click()}
              >
                <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {file ? file?.name : 'Drop your CSV file here'}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse files
                </p>
                <Button variant="outline" size="sm">
                  Select File
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {importStep === 'preview' && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-semibold text-foreground">{importData?.length}</p>
                  <p className="text-sm text-muted-foreground">Total Records</p>
                </div>
                <div className="text-center p-4 bg-success/10 rounded-lg">
                  <p className="text-2xl font-semibold text-success">
                    {importData?.filter(item => item?.status === 'valid')?.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Valid</p>
                </div>
                <div className="text-center p-4 bg-error/10 rounded-lg">
                  <p className="text-2xl font-semibold text-error">
                    {importData?.filter(item => item?.status === 'error')?.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
              </div>

              {/* Data Preview */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">Data Preview</h3>
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Row</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importData?.map((item) => (
                          <tr key={item?.row} className="border-t border-border">
                            <td className="px-4 py-3 text-sm text-foreground">{item?.row}</td>
                            <td className="px-4 py-3 text-sm text-foreground">{item?.name}</td>
                            <td className="px-4 py-3 text-sm text-foreground">{item?.email}</td>
                            <td className="px-4 py-3 text-sm text-foreground">{item?.role}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <Icon 
                                  name={item?.status === 'valid' ? 'CheckCircle' : 'XCircle'} 
                                  size={16} 
                                  className={item?.status === 'valid' ? 'text-success' : 'text-error'} 
                                />
                                {item?.status === 'error' && item?.errors && (
                                  <div className="text-xs text-error">
                                    {item?.errors?.[0]}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {importStep === 'processing' && (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-foreground mb-2">Processing Import</h3>
              <p className="text-sm text-muted-foreground">
                Creating user accounts and sending welcome emails...
              </p>
            </div>
          )}

          {importStep === 'complete' && importResults && (
            <div className="space-y-6">
              {/* Results Summary */}
              <div className="text-center">
                <Icon name="CheckCircle" size={64} className="text-success mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Import Complete</h3>
                <p className="text-sm text-muted-foreground">
                  User import has been processed successfully
                </p>
              </div>

              {/* Results Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-semibold text-foreground">{importResults?.total}</p>
                  <p className="text-sm text-muted-foreground">Total Processed</p>
                </div>
                <div className="text-center p-4 bg-success/10 rounded-lg">
                  <p className="text-2xl font-semibold text-success">{importResults?.successful}</p>
                  <p className="text-sm text-muted-foreground">Successful</p>
                </div>
                <div className="text-center p-4 bg-error/10 rounded-lg">
                  <p className="text-2xl font-semibold text-error">{importResults?.failed}</p>
                  <p className="text-sm text-muted-foreground">Failed</p>
                </div>
              </div>

              {/* Error Details */}
              {importResults?.errors?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Failed Records</h4>
                  <div className="space-y-2">
                    {importResults?.errors?.map((error) => (
                      <div key={error?.row} className="p-3 bg-error/10 border border-error/20 rounded-lg">
                        <p className="text-sm font-medium text-error">Row {error?.row}: {error?.name}</p>
                        <ul className="text-xs text-error/80 mt-1">
                          {error?.errors?.map((err, index) => (
                            <li key={index}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {importStep === 'preview' && `${importData?.filter(item => item?.status === 'valid')?.length} users ready to import`}
            {importStep === 'complete' && 'Import process completed'}
          </div>
          
          <div className="flex items-center space-x-3">
            {importStep === 'upload' && (
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            )}
            
            {importStep === 'preview' && (
              <>
                <Button variant="outline" onClick={() => setImportStep('upload')}>
                  Back
                </Button>
                <Button 
                  variant="default" 
                  onClick={handleImport}
                  disabled={importData?.filter(item => item?.status === 'valid')?.length === 0}
                >
                  Import {importData?.filter(item => item?.status === 'valid')?.length} Users
                </Button>
              </>
            )}
            
            {importStep === 'complete' && (
              <Button variant="default" onClick={handleClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserImportModal;